import React, { useMemo, useState } from 'react';
import { Card, Table, Typography, Button, Space, Progress, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { MOCK_PROJECTS } from './mockProjects';
import TargetTags from './TargetTags';
import useUIDesign from './useUIDesign';
import { UI_STATUS } from './uiDesignMock';
import { INITIAL_BUILD_STATES } from './buildConst';
import { customProgress } from './customStatus';
import { hasProjectApp } from './appMock';
import useAudit from './useAudit';
import ApplyModal from './ApplyModal';

const { Title, Text } = Typography;

const stageProgressColumn = (title, key) => ({
    title, key, width: 140,
    render: (_, r) => {
        const s = r[key] || {};
        const percent = s.total ? Math.round((s.done / s.total) * 100) : 0;
        return (
            <div>
                <Progress percent={percent} size="small" style={{ marginBottom: 2 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{s.done}/{s.total} 项</Text>
            </div>
        );
    },
});

const MyCustom = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState(MOCK_PROJECTS);
    const [apply, setApply] = useState({ open: false, project: null });
    const { getStatus } = useUIDesign();
    const { getStatus: getAuditStatus, submit, resubmit } = useAudit();

    const columns = useMemo(() => {
        // 当前进度：聚合「审核 / 资料 / UI 确认 / 阶段构建 / 应用创建」为企业可读的状态
        const progressOf = (r) => customProgress({
            auditStatus: getAuditStatus(r.id),
            p1: r.p1 || { done: 0, total: 0 },
            p2: r.p2 || { done: 0, total: 0 },
            uiStatus: getStatus(r.id),
            phase1: INITIAL_BUILD_STATES[`${r.id}:phase1`],
            phase2: INITIAL_BUILD_STATES[`${r.id}:phase2`],
            appCreated: hasProjectApp(r.id),
        });

        return [
            { title: '应用名称', dataIndex: 'appName', width: 130, render: (v) => <Text strong>{v}</Text> },
            {
                title: '当前进度', key: 'progress', width: 200,
                render: (_, r) => {
                    const p = progressOf(r);
                    return (
                        <div>
                            <Tag color={p.color} style={{ border: 'none', fontWeight: p.owner === 'company' ? 600 : 400 }}>{p.text}</Tag>
                            <div><Text type="secondary" style={{ fontSize: 12 }}>{p.hint}</Text></div>
                        </div>
                    );
                },
            },
            { title: '定制范围', key: 'targets', width: 165, render: (_, r) => <TargetTags targets={r.targets} /> },
            stageProgressColumn('第一阶段资料', 'p1'),
            stageProgressColumn('第二阶段资料', 'p2'),
            {
                title: 'UI设计', key: 'ui', width: 110,
                render: (_, r) => {
                    const info = UI_STATUS[getStatus(r.id)];
                    return <Tag color={info.color} style={{ border: 'none' }}>{info.text}</Tag>;
                },
            },
            { title: '创建时间', dataIndex: 'createdAt', width: 130, render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text> },
            {
                title: '操作', key: 'action', width: 145, fixed: 'right',
                render: (_, r) => {
                    const p = progressOf(r);
                    return (
                        <Space size={8} wrap={false}>
                            {p.owner === 'company' && (
                                p.key === 'audit_rejected'
                                    ? <Button type="primary" size="small" onClick={() => setApply({ open: true, project: r })}>编辑并重提</Button>
                                    : <Button type="primary" size="small"
                                        onClick={() => navigate(`/app/oem-custom/detail/${r.id}`, { state: { ...p.target, project: r } })}>去处理</Button>
                            )}
                            <Button type="link" size="small" style={{ padding: 0 }}
                                onClick={() => navigate(`/app/oem-custom/detail/${r.id}`, { state: { project: r } })}>查看详情</Button>
                        </Space>
                    );
                },
            },
        ];
    }, [navigate, getStatus, getAuditStatus]);

    const handleSubmit = (values) => {
        if (apply.project) {
            // 编辑重提：更新项目信息 → 回到「待审核」
            const id = apply.project.id;
            setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, appName: values.appName, targets: values.targets } : p)));
            resubmit(id);
            setApply({ open: false, project: null });
            message.success('已重新提交，等待平台审核');
            return;
        }
        const id = `OEM-${Date.now().toString().slice(-6)}`;
        const newProject = {
            id,
            appName: values.appName,
            companyName: 'test-本企业',
            companyId: 'test-SELF',
            targets: values.targets,
            stage: 'phase1',
            p1: { done: 0, total: 0 },
            p2: { done: 0, total: 0 },
            createdAt: new Date().toLocaleString('zh-CN', { hour12: false }),
            doneInStage: 0,
        };
        setProjects((prev) => [newProject, ...prev]);
        submit(id, newProject);
        setApply({ open: false, project: null });
        message.success('定制申请已提交，等待平台审核通过后可填写资料');
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>我的定制</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setApply({ open: true, project: null })}>发起定制</Button>
            </div>

            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Table
                    columns={columns}
                    dataSource={projects}
                    rowKey="id"
                    pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                    scroll={{ x: 1160 }}
                    locale={{ emptyText: '暂无定制项目，点击右上角「发起定制」开始' }}
                />
            </Card>

            <ApplyModal
                open={apply.open}
                project={apply.project}
                onCancel={() => setApply({ open: false, project: null })}
                onSubmit={handleSubmit}
            />
        </div>
    );
};

export default MyCustom;
